"use client";

import React, { useState } from "react";
import Image from "next/image";
import { Section, Typography } from "@/components/ui";
import { env } from "@/lib/environment";
import { ButtonConfig } from "../ui/HeroSection";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

export type ChannelUseCase = {
  /** Tab label shown in the tab bar */
  tabLabel: string;
  /** Main title for the content panel */
  title: string;
  /** Description paragraph below the title */
  description: string;
  /** Optional: description for mobile instead of image; desktop keeps image */
  descriptionMobile?: string;
  /** Bullet points with checkmark pointers */
  pointers: string[];
  image: string;
  imageAlt: string;
  /** Optional: use on mobile instead of image; desktop keeps image */
  imageMobile?: string;
  imageWidth?: number;
  imageHeight?: number;
};

interface ChannelUseCasesCarouselProps {
  /** Section heading */
  sectionTitle: string;
  /** Section subtitle */
  sectionSubtitle: string;
  useCases: ChannelUseCase[];
  sectionClassName?: string;
  buttons?: ButtonConfig[];
  /** Autoplay interval in ms for mobile (default 5000). Set to 0 to disable. */
  autoplayInterval?: number;
}

const checkIconUrl = `${env.NEXT_PUBLIC_CDN_URL}/images/home/circle-check-primary.jV45drQ6h6ca.svg`;
const DEFAULT_AUTOPLAY_INTERVAL = 50000;

export function ChannelUseCasesCarousel({
  sectionTitle,
  sectionSubtitle,
  useCases,
  sectionClassName = "",
  buttons,
  autoplayInterval = DEFAULT_AUTOPLAY_INTERVAL,
}: ChannelUseCasesCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!useCases || useCases.length === 0) return null;

  const activeCase = useCases[activeIndex];

  return (
    <Section variant="default" className={sectionClassName || undefined}>
      <div className="container mx-auto max-w-6xl">
        {/* Section Heading */}
        <div className="text-center mb-6 md:mb-10">
          <Typography variant="h2" as="h2" className="mb-4">
            {sectionTitle}
          </Typography>
          <Typography
            variant="body"
            as="p"
            className="text-text-secondary max-w-3xl mx-auto"
          >
            {sectionSubtitle}
          </Typography>
        </div>

        {/* Mobile: swipeable cards using Swiper */}
        <div className="md:hidden mb-10 w-full">
          <Swiper
            modules={[Autoplay]}
            spaceBetween={16}
            slidesPerView={1.2}
            autoplay={
              autoplayInterval > 0 && useCases.length > 1
                ? {
                  delay: autoplayInterval,
                  disableOnInteraction: false,
                }
                : false
            }
            loop={useCases.length > 1}
            className="w-full"
          >
            {useCases.map((useCase, index) => (
              <SwiperSlide key={useCase.tabLabel}>
                <div className="bg-white h-[100dvh] rounded-xl border border-gray-100 overflow-hidden">
                    <div className="w-full relative h-38">
                      <Image
                        src={useCase.imageMobile ?? useCase.image}
                        alt={useCase.imageAlt}
                        title={useCase.imageAlt}
                        width={useCase.imageWidth || 900}
                        height={useCase.imageHeight || 520}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Channel label pill */}
                    <div className="p-4 w-full flex flex-col justify-start items-start  ">
                    <div
                      className="px-3  mt-2 py-1 bg-orange-300/25 text-red-600  font-medium rounded-lg text-xs border border-gray-200 mb-4"
                    >
                      {useCase.tabLabel}
                    </div>
                    <Typography
                      variant="h3"
                      as="h3"
                      className="text-lg font-semibold text-text-primary mb-2"
                    >
                      {useCase.title}
                    </Typography>
                    <Typography
                      variant="body"
                      as="p"
                      className="text-text-secondary text-[13px] ms:text-base leading-relaxed mb-6"
                    >
                      {useCase.descriptionMobile ?? useCase.description}
                    </Typography>
                    {useCase.pointers && useCase.pointers.length > 0 && (
                      <ul className="space-y-3 mb-4">
                        {useCase.pointers.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Image
                              src={checkIconUrl}
                              alt=""
                              aria-hidden
                              width={13}
                              height={13}
                              className="flex-shrink-0 mt-0.5"
                            />
                            <Typography
                              variant="body"
                              as="span"
                              className="text-text-primary text-sm"
                            >
                              {item}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    )}
                    </div>

                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Desktop / Tablet: tabbed layout with single content panel */}
        <div className="hidden md:block">
          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {useCases.map((useCase, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={useCase.tabLabel}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive
                      ? "bg-orange-300/25 text-red-600 font-semibold"
                      : "text-text-primary border border-gray-200 hover:border-primary"
                    }`}
                >
                  {useCase.tabLabel}
                </button>
              );
            })}
          </div>

          {/* Content Panel */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col lg:flex-row">
            {/* Left - Title, description, pointers */}
            <div className="w-full lg:w-[52%] px-8 py-10 flex flex-col justify-center">
              <Typography
                variant="h3"
                as="h3"
                className="text-xl md:text-2xl font-semibold text-text-primary mb-4"
              >
                {activeCase.title}
              </Typography>
              <Typography
                variant="body"
                as="p"
                className="text-text-secondary text-base leading-7 mb-6"
              >
                {activeCase.description}
              </Typography>
              {activeCase.pointers && activeCase.pointers.length > 0 && (
                <ul className="space-y-3">
                  {activeCase.pointers.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Image
                        src={checkIconUrl}
                        alt=""
                        aria-hidden
                        width={16}
                        height={16}
                        className="flex-shrink-0 mt-0.5"
                      />
                      <Typography
                        variant="body"
                        as="span"
                        className="text-text-primary text-base"
                      >
                        {item}
                      </Typography>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Right - Image */}
            <div className="w-full lg:w-[58%] relative">
              <Image
                src={activeCase.image}
                alt={activeCase.imageAlt}
                title={activeCase.imageAlt}
                width={activeCase.imageWidth || 900}
                height={activeCase.imageHeight || 520}
                className="w-full h-full object-cover min-h-[320px] md:min-h-[400px]"
              />
            </div>
          </div>

          {/* Desktop CTA buttons under carousel */}
          {buttons && buttons.length > 0 && (
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              {buttons.map((button, index) => (
                <a
                  key={index}
                  href={button.href || "/book-a-demo"}
                  target={button.target || "_self"}
                  className={
                    button.variant === "primary" ? "btn-dark btn-md" : "btn-outline-primary btn-md"
                  }
                >
                  {button.text}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}
